/** JSON 데이터 파일들의 스키마. data/*.json 과 1:1 대응. */

export type ShapeName = 'circle' | 'square' | 'triangle' | 'diamond' | 'star' | 'hexagon' | 'cross';

/** 살바트리스 마나 속성. 이펙트 색 램프가 여기서 결정된다. */
export type Attr = 'red' | 'blue' | 'yellow' | 'grey' | 'brown' | 'white' | 'steel';

/**
 * 이펙트 모양. core/skillfx.ts 가 코드로 찍어내고, game/looks.ts 가 소리를 붙인다.
 * 스킬마다 하나씩이다 — 같은 속성끼리도 한눈에 구분되게.
 */
export type FxKind =
  // 베기
  | 'crescent' | 'frostfan' | 'lash' | 'windcut' | 'hammer' | 'thrust' | 'dash' | 'greatsword' | 'whirl'
  // 탄
  | 'ember' | 'shard' | 'dart' | 'lightning' | 'bubble' | 'chunk' | 'fireball' | 'wave' | 'jet' | 'quake' | 'rune' | 'royal'
  // 사방으로
  | 'puff' | 'gustarc' | 'leaf' | 'blade'
  // 궤도 / 오라 / 장판 / 옮겨붙기 / 뛰어들기
  | 'wisp' | 'spirit' | 'obsidian' | 'stomp' | 'cells'
  | 'snow' | 'fog' | 'pillar' | 'snowflake' | 'sigil'
  | 'arc' | 'static' | 'pounce'
  // 거대한 검 (기사) / 잿빛대검 (회광반조)
  | 'greatblade' | 'ashblade'
  // 장풍 (소한공)
  | 'palm'
  // 예전 이름 (기본 도형으로 그린다)
  | 'ring' | 'spark' | 'gust' | 'orb';

/** 적에게 거는 상태효과. 원작 키워드를 그대로 옮겼다. */
export interface StatusApply {
  /** [화염] 중첩. 주기적으로 중첩만큼 피해를 입고 사라진다(burnLock 중에는 남는다). config STATUS.BURN_* */
  burn?: number;
  /**
   * 이 [화염] 한 중첩이 터질 때 주는 피해 = 불을 건 스킬의 공격력 × 그 스킬의 화염 배수(WeaponDef.burnMul).
   * 없으면 예전처럼 고정값(STATUS.BURN_DAMAGE_PER_STACK).
   */
  burnDmg?: number;
  /** [서리] 중첩. 8 이상이면 [빙결] (config STATUS.FROST_FREEZE_AT). */
  frost?: number;
  /** [감전]. 한 번에 큰 피해를 받으면 주변으로 터진다. */
  shock?: number;
  /** [기절] 지속 시간(초). */
  stun?: number;
  /** 이 시간 동안 [화염]이 꺼지지 않는다(초). */
  burnLock?: number;
  /** [화염]이 이만큼 쌓이면 터져 옆으로 옮겨붙는다 (회광반조 점화). 없으면 안 터진다 */
  ignite?: number;
  /** 1 이상이면 이 [서리]로는 얼리지 못한다 — 얼기 바로 전까지만 쌓인다 (소한공 "시리게 만들 뿐이다") */
  chill?: number;
}

export interface CharacterDef {
  id: string;
  name: string;
  title?: string;
  description: string;
  maxHp: number;
  moveSpeed: number;
  /** 시작 무기 id. 빈 문자열이면 무기 없이 시작한다(준 — 맵에 떨어진 경험치를 주워 첫 레벨업에서 무기를 고른다) */
  startWeapon: string;
  passive?: string;
  /** 도트 아틀라스 JSON 경로. 있으면 sprite/shape 대신 이걸 쓴다. */
  atlas?: string;
  /** 아틀라스 프레임 번호 매핑. */
  anim?: {
    idleDown: number[];
    idleUp: number[];
    idleSide: number[];
    walkDown: number[];
    walkUp: number[];
    walkSide: number[];
    attack: number[];
    cast: number[];
    dead: number[];
    /**
     * 프레임마다 발을 세울 가로 위치(그림 폭 대비 0~1). 없으면 0.5(가운데).
     * 칼자국 같은 효과가 한쪽으로 길게 그려진 프레임은 몸이 가운데에 있지 않아서, 안 주면 자세마다 몸이 옆으로 튄다.
     */
    anchorX?: Record<string, number>;
    /** 왼쪽을 보고 그려진 프레임 번호들. 게임은 옆모습이 오른쪽을 본다고 보고 그리므로, 이 프레임은 좌우를 뒤집는다 */
    mirror?: number[];
  };
  /** 결과·레벨업 화면에 쓸 표정 프레임 번호. */
  portraits?: number[];
  /**
   * 변신한 모습들. 무기(고유 스킬)의 form 이 여기 이름과 같으면, 그 무기로 변신한 동안 이 모습이 된다.
   * transform 은 바뀌는 순간 한 번 재생하는 프레임, untransform 은 풀릴 때 재생하는 프레임(둘 다 이 모습의 아틀라스 번호, 없으면 바로 바뀐다).
   * aura 면 그림 위에 불꽃 테두리·잿불·검붉은 몸빛을 덧입힌다(따로 그린 변신 그림이 없을 때 — 템페스).
   * 여기 없는 캐릭터도 변신은 한다: 원래 그림 그대로 aura 를 입는다(game.ts formOf).
   */
  forms?: Record<string, {
    atlas: string; anim: NonNullable<CharacterDef['anim']>; transform?: number[]; untransform?: number[]; aura?: boolean;
  }>;
  /**
   * 기본 특성: 피해 배율 가감. slash = 베기(모션 기준 — 근접·거대한 검·잿빛대검), magic = 마법(그 밖, 도약·장풍 제외).
   * xp = 경험치 획득 배율 가감 (패시브 칸을 차지하지 않는 캐릭터 자체 패시브). damage = 모든 피해 배율 가감(인챈트와 더해진다).
   * 예: 노아 { slash: 0.1, magic: -0.1 } = 베기 +10%, 마법 -10% / 시 { xp: 0.1 } = 경험치 +10% / 템페스 { damage: 0.05 } = 공격력 +5%
   * choices = 레벨업·보물 상자 카드 수 (기본 3 — 1.23 준 4)
   */
  trait?: { slash?: number; magic?: number; xp?: number; damage?: number; choices?: number };
  /** 아틀라스가 없을 때만 쓰는 대체 그림. */
  sprite?: string;
  color: string;
  shape: ShapeName;
}

export type WeaponBehavior =
  | 'projectile' | 'orbit' | 'aura' | 'nova' | 'melee' | 'field' | 'chain'
  /** 적이 가장 두꺼운 곳으로 순간이동하며 착지 지점을 터뜨린다 */
  | 'blink'
  /** 가장 가까운 적 쪽으로 번개가 한 번에 내리꽂힌다. 그 줄 위의 적 전부가 맞는다 (여제의 명령) */
  | 'strike'
  /** 거대한 검을 불러 주변을 끌어당긴 뒤 몇 번이고 휘둘러 벤다 (기사) */
  | 'greatblade'
  /** 잿빛대검을 크게 휘두른다. 지나간 호를 따라 바닥에 검상이 남아 타오른다 (회광반조) */
  | 'ashblade'
  /** 손을 내질러 앞으로 한기를 쏜다 — 응상(짧은 띠) → 쇄상(긴 띠, [서리]를 녹여 더 아프게) (소한공) */
  | 'palm'
  /** 액티브: 플레이어가 Shift(폰은 화면 버튼)로 쓴다. 이동 방향으로 도약해 착지 자리를 터뜨린다 (사냥 준비) */
  | 'leap'
  /**
   * 1.24 전격 질주 — 누구나 가진 기본 대시를 번개 대시로 바꾼다(Shift). 가진 동안 대시가 길어지고,
   * 지나간 줄의 적을 긋고, 지나간 자리에 전기 잔류가 남는다. 저절로 나가지 않는다(플레이어가 누를 때만)
   */
  | 'dash'
  /**
   * 불·얼음·번개·모래 회오리 넷이 몸 둘레를 휘몰아치고(radius = 궤도, size = 회오리 크기, cooldown = 한 적을 다시 치는 간격),
   * pulse 초마다 넷이 모여 한꺼번에 터진다(조화) — 광풍. 그림을 못 읽으면 melee 로 돈다
   */
  | 'tempest';

/** 무기 수치. 레벨업 때 levels[] 값이 더해진다. */
export interface WeaponStats {
  damage: number;
  cooldown: number;
  speed: number;
  count: number;
  pierce: number;
  area: number;
  size: number;
  radius: number;
  arc: number;
  duration: number;
  slow: number;
  knockback: number;
  /** 탄이 터질 때의 반경(px). 0이면 안 터진다. */
  explodeRadius: number;
  /** 폭발 피해 = damage * 이 비율 */
  explodeRatio: number;
  /** 입힌 피해의 이 비율만큼 체력을 회복한다 (0~1) */
  lifesteal: number;
  /** 이 무기를 들고 있는 동안(또는 장판 안에 있는 동안) 받는 피해 감소 (0~1) */
  guard: number;
  /** 0보다 크면 이 무기로 붙인 [화염]이 한동안 꺼지지 않는다 */
  burnLock: number;
  /** chain 전용. 전류가 몇 줄기로 나가는가 (각 줄기는 서로 다른 적에서 시작한다) */
  volley: number;
  /** chain 전용. 번개 굵기 배율(1 = 보통). 2 이상이면 굵은 번개 — 선·튀는 자리가 커지고 소리도 커진다 */
  bolt: number;
  /** projectile/nova 전용. 탄의 좌우 반폭(px). 0이면 둥근 탄(size 로만 맞는다). 넘실이는 파도가 옆으로 길어지는 값 */
  width: number;
  /** nova 전용. 1 이상이면 무작위 각도 대신 십자(+) 방향으로 쏜다 */
  cross: number;
  /** leap 전용. 도약 거리(px) */
  leap: number;
  /** leap 전용. 모아 둘 수 있는 도약 횟수 (쿨타임마다 하나씩 찬다) */
  charges: number;
  /** field 전용. 1 이상이면 장판이 사는 동안 플레이어를 따라다닌다 */
  follow: number;

  // ── 1.11 고유 스킬·갈래가 모양을 바꾸는 값들 (0 = 없음) ──
  /** 이 반경 안의 경험치 보석을 빨아들인다 (광풍) */
  vacuum: number;
  /** 1 이상이면 공격 범위 안으로 날아든 적 탄을 흩어 없앤다 (광풍 「폭풍의 눈」) */
  deflect: number;
  /** 1 이상이면 검이 나와 있는 동안 범위 안의 적 탄을 되돌려 보낸다 (기사) */
  reflect: number;
  /** 벨 때마다 지나간 자리에 남는 잿불 자국 수 (회광반조, 화염 채찍 「불길 도배」) */
  trail: number;
  /** 1 이상이면 탄이 터진 자리에 불이 남는다 (플레임 스로우 「불바다」) */
  pool: number;
  /** 1 이상이면 탄이 하늘에서 떨어진다 — 적 머리 위로 내리꽂혀 그 자리가 터진다 (유성우·파편 비·뇌우) */
  fall: number;
  /** 얼어붙은 적이 맞으면 산산조각 — 추가 피해 배율 겸 세기 (풍화설영 쇄화) */
  shatter: number;
  /** 처치할수록 몸·범위가 커지는 최대치 (0.5 = 1.5배, 타이탄) */
  grow: number;
  /** 이 간격(초)마다 기절 충격파 (타이탄) */
  pulse: number;
  /** 이 간격(초)마다 영역 안 가장 센 적에게 황금 광선 (메자이) */
  beam: number;
  /** 영역 안에 있으면 초당 최대 체력의 이 비율만큼 회복 (메자이) */
  zoneHeal: number;
  /** orbit 전용. 1 이상이면 고위 정령 — 크고 세고 그림이 다르다 (다중 소환 유지 「고위 정령」) */
  tier: number;
  /** orbit 전용. 0보다 크면 정령왕(불·얼음·번개) 셋을 불러낸다. 값은 정령왕 공격의 피해 배율 (「정령왕」) */
  king: number;
  /** 넘치는 회복을 보호막으로 — 최대 체력의 이 비율까지. 깨질 때 세포 폭발 (세포 역설) */
  overheal: number;
  /** 번개 자리에 이 시간(초) 전기장이 남고, [자기장]에 묶인 적끼리 전류로 이어진다 (여제의 명령) */
  storm: number;
  // ── 1.20 회광반조: Space 로 켜고 끄는 변신 ──
  /** 변신이 이어지는 시간(초) — 기력 100 이 이만큼에 다 닳는다. 0 이면 변신 없이 가진 동안 늘 그 모습(예전 방식) */
  formTime: number;
  /** 기력이 0 에서 가득 차기까지(초) */
  formRecover: number;
  /** 변신하지 않은 동안 대검을 이 비율의 피해로 느리게 휘두른다(잔불). 0 이면 변신해야만 휘두른다 */
  ember: number;
  /** 변신하는 순간 둘레가 터진다 — 피해 배율(× damage) */
  formBurst: number;
}

/**
 * 진화 갈림길의 한 갈래. 고른 쪽의 수치만 더해진다.
 * 카드에는 name 이 제목, _desc 가 설명으로 뜬다.
 */
export interface BranchOption extends Partial<WeaponStats> {
  id: string;
  name: string;
  _desc: string;
  status?: StatusApply;
  /**
   * 앞 갈림길에서 이 중 하나를 골랐을 때만 나오는 갈래 (갈래 id). 없으면 늘 나온다.
   * 조건을 거르고 한 갈래만 남으면 고를 것 없이 평범한 강화 카드로 뜬다.
   */
  needs?: string[];
}

/**
 * 레벨 하나에서 더해지는 것. choose 가 있으면 진화 갈림길 — 둘(이상) 중 하나를 골라 그 수치만 더한다.
 */
export type WeaponLevel = Partial<WeaponStats> & {
  _desc?: string;
  status?: StatusApply;
  choose?: BranchOption[];
};

export interface WeaponDef {
  id: string;
  name: string;
  description: string;
  /**
   * 화염 배수 — 이 스킬이 건 [화염] 한 중첩의 피해 = 이 스킬 한 방 공격력 × burnMul.
   * 스킬마다 따로 둬서, 화염이 너무 센 스킬은 한 방 피해를 건드리지 않고 이것만 낮춘다(tools/levels_design.py BURN).
   */
  burnMul?: number;
  /** 원작 시트에서의 출처. 화면에 작게 표시한다. */
  origin?: string;
  behavior: WeaponBehavior;
  /** 이펙트 색 램프 */
  attr: Attr;
  /** 이펙트 모양 */
  fx: FxKind;
  /** 이 무기를 가진 동안 캐릭터가 이 모습으로 변신한다 (CharacterDef.forms 의 이름) */
  form?: string;
  /** 이 캐릭터들만 쓸 수 있다(캐릭터 id). 없으면 누구나. 예: 복마검은 적소하 전용 */
  only?: string[];
  /** 이 캐릭터들은 못 쓴다(캐릭터 id). 예: 삼재검은 적소하 빼고 */
  except?: string[];
  /**
   * 조합 재료를 고를 때 이 속성으로도 쳐 준다 (1.23). 무기의 이펙트 색(attr)은 그대로 두고 조합 자리만 넓힌다.
   * 비급 검 둘이 서로의 자리를 대신한다: 복마검(red) ↔ 삼재검(white) — 한 캐릭터가 둘을 같이 얻는 일은 없으므로(only/except),
   * 이게 없으면 적소하만 검으로 red 조합(회광반조)을 열 수 있었다.
   */
  fusionAs?: Attr[];
  maxLevel: number;
  sprite?: string;
  color: string;
  shape: ShapeName;
  base: Partial<WeaponStats>;
  /** 명중한 적에게 거는 상태효과 */
  status?: StatusApply;
  /** levels[0] 이 레벨 2로 올릴 때 더해지는 값. 길이 = maxLevel - 1 */
  levels: WeaponLevel[];
}

/** 조합 조건. 재료가 될 수 있는 무기(Lv6 이상, 만렙이 더 낮으면 만렙 — CONFIG.FUSION_MIN_LEVEL) 중 여기에 맞는 것이 count 개 있으면 조합할 수 있다. */
export interface FusionReq {
  /** 같은 속성끼리 */
  attr?: Attr;
  /** 같은 동작끼리 (근접 3개 등) */
  behavior?: WeaponBehavior;
  /** 딱 이 무기들 */
  ids?: string[];
  /** 재료로 쓸 무기 총 개수 */
  count?: number;
  /**
   * 그중 attr/behavior 조건을 만족해야 하는 최소 개수.
   * 안 적으면 전부 만족해야 한다. 1을 주면 "하나만 섞여 있으면 된다".
   */
  match?: number;
}

/** 조합으로 나오는 고유 스킬. 무기와 같은 틀을 쓰고 조건만 더 붙는다. */
export interface FusionDef extends WeaponDef {
  requires: FusionReq;
}

export interface PassiveMods {
  /** [견고]. 받는 피해 절대 감소. armorAdd 와 같은 계열. */
  toughAdd?: number;
  /** 부여하는 [화염]/[서리] 중첩 증가 */
  burnAdd?: number;
  frostAdd?: number;
  damageMul?: number;
  moveSpeedMul?: number;
  cooldownMul?: number;
  areaMul?: number;
  projectileSpeedMul?: number;
  pickupMul?: number;
  xpMul?: number;
  maxHpAdd?: number;
  armorAdd?: number;
  regenAdd?: number;
  luckAdd?: number;
}

export interface PassiveDef {
  id: string;
  name: string;
  description: string;
  origin?: string;
  attr?: Attr;
  maxLevel: number;
  sprite?: string;
  color: string;
  shape: ShapeName;
  mods: PassiveMods;
}

/**
 * 적이 어떻게 덤비는가. 장르에서 이미 검증된 역할들만 골랐다.
 *  chase    — 곧장 달려든다 (기본. 수로 밀어붙이는 놈들)
 *  charger  — 멈춰서 조준하고 직선으로 돌진한다 (옆으로 피해야 한다)
 *  orbiter  — 빙 돌면서 파고든다 (정면 무기로 맞추기 까다롭다)
 *  ranged   — 거리를 두고 쏜다 (쫓아가서 끊어야 한다)
 *  splitter — 죽으면 작은 놈 여럿으로 쪼개진다 (광역기 없으면 늘어난다)
 *  exploder — 죽을 때 터진다 (가까이서 죽이면 내가 맞는다)
 *  charmer  — 1.22 요화: 거리를 두고 돌며 하트를 날린다. 맞으면 [매혹] — 그 요괴 쪽으로 천천히 걸어가고 조작이 안 된다
 *  bruiser  — 1.22 거상: 쫓아오다 가까우면 멈춰 앞발을 들고(경고) 부채꼴로 할퀸다
 *  boss     — 1.22 보스 셋: 경고를 먼저 보여 주고 치는 기술들 (game/bosses.ts, pattern 으로 고른다)
 */
export type EnemyAi = 'chase' | 'charger' | 'orbiter' | 'ranged' | 'splitter' | 'exploder' | 'charmer' | 'bruiser' | 'boss';

export interface EnemyDef {
  id: string;
  name: string;
  hp: number;
  damage: number;
  speed: number;
  radius: number;
  xp: number;
  color: string;
  shape: ShapeName;
  sprite?: string;
  minTime: number;
  maxTime: number;
  weight: number;
  boss?: boolean;

  /** 덤비는 방식. 없으면 chase. */
  ai?: EnemyAi;
  /** 1.22 도트 그림 (public/assets/enemies/<art>.json, tools/slice_enemies.py). 없으면 silhouette 도형 */
  art?: string;
  /** 그림 크기 배율 (기본 1 — 아틀라스가 이미 게임 크기다) */
  artScale?: number;
  /** boss: 기술 묶음 */
  pattern?: 'gatekeeper' | 'rakshasa' | 'king' | 'ryu';
  /** 실루엣. 없으면 blob. */
  silhouette?: 'blob' | 'spike' | 'wisp' | 'armored' | 'sac' | 'bulb' | 'eye';

  // charger
  /** 이 거리 안에 들어오면 돌진을 준비한다 */
  chargeRange?: number;
  /** 돌진 중 속도 배율 */
  chargeSpeed?: number;

  // ranged
  /** 유지하려는 거리 */
  keepDistance?: number;
  shotInterval?: number;
  shotSpeed?: number;
  shotDamage?: number;

  // splitter
  /** 죽을 때 쪼개지는 수 */
  splitCount?: number;
  /** 쪼개져 나오는 적 (없으면 같은 적을 작게) */
  splitInto?: string;

  // ranged
  /** 이만큼마다 한 번은 모아서 크게 쏜다 (권귀의 큰 장풍) */
  chargeEvery?: number;
  /** 탄 그림 (fx 아틀라스 이름). 없으면 둥근 탄 */
  shot?: string;

  // charmer
  /** [매혹] 시간(초) */
  charmTime?: number;

  // bruiser
  /** 이 거리 안이면 멈춰서 할퀸다 */
  attackRange?: number;

  // exploder
  /** 죽을 때 터지는 반경 */
  deathRadius?: number;
  /** 터질 때 플레이어가 받는 피해 */
  deathDamage?: number;
}

/** 한 판의 결과. 3단계에서 이 모양 그대로 서버에 보낸다. */
export interface RunResult {
  characterId: string;
  survivalSec: number;
  kills: number;
  level: number;
  score: number;
  /** 넘긴 보스 수 */
  bossesDown: number;
}

export const DEFAULT_WEAPON_STATS: WeaponStats = {
  damage: 10,
  cooldown: 1,
  speed: 400,
  count: 1,
  pierce: 0,
  area: 1,
  size: 8,
  radius: 90,
  arc: 90,
  duration: 0,
  slow: 0,
  knockback: 0,
  explodeRadius: 0,
  explodeRatio: 0.6,
  lifesteal: 0,
  guard: 0,
  burnLock: 0,
  volley: 1,
  bolt: 1,
  width: 0,
  cross: 0,
  leap: 0,
  charges: 1,
  follow: 0,
  vacuum: 0,
  deflect: 0,
  reflect: 0,
  trail: 0,
  pool: 0,
  fall: 0,
  shatter: 0,
  grow: 0,
  pulse: 0,
  beam: 0,
  zoneHeal: 0,
  tier: 0,
  king: 0,
  overheal: 0,
  storm: 0,
  formTime: 0,
  formRecover: 0,
  ember: 0,
  formBurst: 0,
};
